import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Copy, type LucideIcon } from 'lucide-react';
import type {
  RDAPLookupResponse,
  RDAPDomainResult,
  RDAPAvailabilityStatus,
} from '../../../types/rdap';
import type { BaseToolResult } from '../../../types/toolResults';
import Tooltip from '../../common/Tooltip';

/**
 * Tool result shape for the `rdap_check_availability` agent tool.
 *
 * Combines the wire-format `RDAPLookupResponse` (committed in AC-1439) with the
 * `BaseToolResult` discriminator fields (`toolType`, `toolName`) that the
 * dispatcher in `ToolResultsDisplay.tsx` uses to route to this renderer.
 *
 * Defined here (rather than in `types/toolResults.ts`) because AC-1443's scope
 * is the renderer only -- AC-1444 owns the dispatcher wiring and is the ticket
 * that registers this type in the `ToolResult` discriminated union.
 */
export interface RDAPCheckAvailabilityToolResult extends BaseToolResult, RDAPLookupResponse {
  toolType: 'rdap_check_availability';
  toolName: string;
}

/**
 * Props shape for this renderer.
 *
 * Mirrors `ToolResultRendererProps` from `types/toolResults.ts` but is
 * declared locally because that type's generic constraint requires `T` to
 * extend the closed `ToolResult` discriminated union -- and the new
 * `RDAPCheckAvailabilityToolResult` member of that union is added by
 * AC-1444 (separate ticket). Once AC-1444 lands the renderer can switch to
 * `ToolResultRendererProps<RDAPCheckAvailabilityToolResult>` without any
 * call-site changes.
 */
interface DomainRDAPAvailabilityRendererProps {
  result: RDAPCheckAvailabilityToolResult;
  onCopy?: (text: string) => void;
  className?: string;
}

/**
 * Renderer for `rdap_check_availability` tool results.
 *
 * Displays a three-state per-domain availability list: `available` / `taken` /
 * `unknown`. The PRD calls out user confusion of `unknown` with `available` as
 * the canonical failure mode for this feature -- the rendering must therefore
 * present `unknown` with **distinct color, icon, and label** from `available`,
 * and surface the machine-readable `reason` (e.g. `no_rdap_server_for_tld`,
 * `rate_limited`, `timeout`) as a human-friendly explanation.
 *
 * Layout mirrors `DomainAvailabilityRenderer.tsx` (the GoDaddy two-state
 * equivalent) so users see a consistent shape across providers; the visual
 * vocabulary is extended with a third row state for `unknown`.
 *
 * Status palette (matches the project's existing severity tokens):
 *   - `available` -> green (`text-green-*`, `CheckCircle`)
 *   - `taken`     -> red   (`text-red-*`,   `XCircle`)
 *   - `unknown`   -> amber (`text-amber-*`, `AlertTriangle`)
 */
const DomainRDAPAvailabilityRenderer: React.FC<DomainRDAPAvailabilityRendererProps> = ({
  result,
  onCopy,
  className = '',
}) => {
  const { total_checked, available_count, taken_count, unknown_count } = result;
  // AC-1456: the step executor stores the per-domain array under
  // `data[<output_field>]` while the agent tool returns it as top-level
  // `results`. Read whichever is populated. The first array-valued entry
  // under `data` wins -- the executor only emits a single key there.
  const dataBag = (result as any).data as Record<string, unknown> | undefined;
  const fromDataBag =
    dataBag && typeof dataBag === 'object'
      ? (Object.values(dataBag).find((v) => Array.isArray(v)) as
          | RDAPLookupResponse['results']
          | undefined)
      : undefined;
  const results = result.results ?? fromDataBag ?? [];

  const handleCopy = (text: string) => {
    if (onCopy) {
      onCopy(text);
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  if (!results || results.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`} role="status">
        <p className="text-gray-500 dark:text-gray-400">No domains checked</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Summary Card */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">
                {result.toolName || 'Domain Availability Check'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {total_checked} domain{total_checked !== 1 ? 's' : ''} checked
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {available_count}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Available</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{taken_count}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Taken</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {unknown_count}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Unknown</p>
            </div>
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Domain
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Detail
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
            {results.map((domainResult, index) => (
              <DomainResultRow
                key={`${domainResult.domain}-${index}`}
                domainResult={domainResult}
                onCopy={handleCopy}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Copy All Available Button */}
      {available_count > 0 && (
        <div className="flex justify-end">
          <button
            onClick={() => {
              const domains = results
                .filter((r) => r.status === 'available')
                .map((r) => r.domain)
                .join('\n');
              handleCopy(domains);
            }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/40 rounded-md transition-colors border border-green-300 dark:border-green-800"
          >
            <Copy className="w-4 h-4" />
            <span>Copy All Available Domains</span>
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Per-row sub-component. Switches on `status` to drive the three-state visual
 * vocabulary. Each row carries an `aria-label` so screen readers get a single
 * coherent announcement combining domain + status + reason.
 */
const DomainResultRow: React.FC<{
  domainResult: RDAPDomainResult;
  onCopy: (text: string) => void;
}> = ({ domainResult, onCopy }) => {
  const { domain, status, reason } = domainResult;
  const visual = STATUS_VISUALS[status];
  const reasonLabel = reason ? humanizeReason(reason) : undefined;

  // Compose accessible row name. e.g. "example.com: available" or
  // "example.xyz: unknown -- no RDAP server for this TLD".
  const ariaLabel = reasonLabel
    ? `${domain}: ${visual.label} -- ${reasonLabel}`
    : `${domain}: ${visual.label}`;

  return (
    <tr
      className={visual.rowClass}
      data-status={status}
      data-testid={`rdap-row-${status}`}
      role="row"
      aria-label={ariaLabel}
    >
      {/* Status Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-2">
          <visual.Icon
            className={`w-5 h-5 ${visual.iconClass}`}
            data-testid={visual.iconTestId}
            aria-hidden="true"
          />
          <span className={`text-sm font-medium ${visual.labelClass}`}>{visual.label}</span>
        </div>
      </td>

      {/* Domain Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-semibold text-gray-900 dark:text-warm-white">{domain}</div>
      </td>

      {/* Detail Column - reason (only meaningful for `unknown`) */}
      <td className="px-6 py-4 whitespace-nowrap">
        {reasonLabel ? (
          <Tooltip content={`Raw reason code: ${reason}`}>
            <span
              className="text-sm text-amber-700 dark:text-amber-300 cursor-help"
              data-testid="rdap-reason"
            >
              {reasonLabel}
            </span>
          </Tooltip>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>

      {/* Actions Column */}
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <Tooltip content="Copy domain name">
          <button
            onClick={() => onCopy(domain)}
            className="inline-flex items-center space-x-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
          >
            <Copy className="w-3 h-3" />
            <span>Copy</span>
          </button>
        </Tooltip>
      </td>
    </tr>
  );
};

/**
 * Visual descriptor per status. Concentrating these here keeps the three-state
 * distinction explicit and easy to audit -- if anyone "fixes" `unknown` to
 * share styling with `available`, this table makes the regression obvious in a
 * single diff.
 */
interface StatusVisual {
  label: string;
  Icon: LucideIcon;
  iconClass: string;
  iconTestId: string;
  labelClass: string;
  rowClass: string;
}

const STATUS_VISUALS: Record<RDAPAvailabilityStatus, StatusVisual> = {
  available: {
    label: 'Available',
    Icon: CheckCircle,
    iconClass: 'text-green-600 dark:text-green-400',
    iconTestId: 'check-circle-icon',
    labelClass: 'text-green-800 dark:text-green-300',
    rowClass:
      'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30',
  },
  taken: {
    label: 'Taken',
    Icon: XCircle,
    iconClass: 'text-red-600 dark:text-red-400',
    iconTestId: 'x-circle-icon',
    labelClass: 'text-red-800 dark:text-red-300',
    rowClass:
      'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800',
  },
  unknown: {
    label: 'Unknown',
    Icon: AlertTriangle,
    iconClass: 'text-amber-600 dark:text-amber-400',
    iconTestId: 'alert-triangle-icon',
    labelClass: 'text-amber-800 dark:text-amber-300',
    rowClass:
      'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30',
  },
};

/**
 * Map RDAP reason codes (TD section 7.1) to human-readable labels.
 *
 * Falls back to a sentence-cased version of the raw code so that any new
 * reason code added on the backend before the frontend catches up still
 * surfaces as readable text.
 */
const REASON_LABELS: Record<string, string> = {
  no_rdap_server_for_tld: 'No RDAP server for this TLD',
  network_error: 'Network error reaching RDAP server',
  timeout: 'RDAP server did not respond in time',
  rate_limited: 'RDAP server rate limited the request',
  server_error: 'RDAP server returned an error',
  malformed_response: 'RDAP server returned a malformed response',
  unexpected_status: 'RDAP server returned an unexpected status',
  malformed_input: 'Domain name is malformed',
  bootstrap_unavailable: 'IANA bootstrap registry is unavailable',
};

function humanizeReason(reason: string): string {
  if (REASON_LABELS[reason]) {
    return REASON_LABELS[reason];
  }
  // Fallback: snake_case -> "Sentence case"
  const spaced = reason.replace(/_/g, ' ').trim();
  if (!spaced) return reason;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export default DomainRDAPAvailabilityRenderer;
